import * as React from 'react'
// libs
import { useMutation, useQuery } from '@apollo/client/react'
// components
import { LoaderWithOverlay } from 'components/UI/Loader'
// context
import { TableManagementContext } from 'containers/Search/layers/BL'
// graphql
import { OBJECT_SECURITIES_QUERY } from 'graphql/objectSecurity/queries'
import {
  OBJECT_SECURITY_CREATE_MUTATION,
  OBJECT_SECURITY_DELETE_MUTATION,
  OBJECT_SECURITY_UPDATE_MUTATION
} from 'graphql/objectSecurity/mutations'
// hooks
import { useCurrentUser } from 'hooks/index'
// types
import { TRefetch } from 'types/refetch'
import { IObjectSecuritiesQueryArgs, IObjectSecurity, IObjectSecurityResponse } from 'types/graphql'
// utils
import get from 'lodash/get'
import { handleError } from 'utils/errors/handlers'
import { getValueFromOption, getValuesFromOptions } from 'utils/form'

type Props = React.PropsWithChildren<unknown>

interface IContext {
  data: IObjectSecurity[]
  refetch: TRefetch<IObjectSecurityResponse, IObjectSecuritiesQueryArgs>
  companyId: string
  isLoading: boolean
  totalCount: number
  isRefetching: boolean
  createObjectSecurity: (data: any, hideModal: () => void) => Promise<void>
  deleteObjectSecurity: (id: string, hideModal: () => void) => Promise<void>
  updateObjectSecurity: (id: string, data: any, hideModal: () => void) => Promise<void>
}

export const ObjectSecurityContext = React.createContext<IContext>({} as any)

const ObjectSecurityAPI: React.FC<Props> = ({ children }) => {
  const user = useCurrentUser()

  const { filter, page, perPage, search } = React.useContext(TableManagementContext)

  const [createObjectSecurityMutation] = useMutation(OBJECT_SECURITY_CREATE_MUTATION)
  const [updateObjectSecurityMutation] = useMutation(OBJECT_SECURITY_UPDATE_MUTATION)
  const [deleteObjectSecurityMutation] = useMutation(OBJECT_SECURITY_DELETE_MUTATION)

  const { data, networkStatus, refetch } = useQuery(OBJECT_SECURITIES_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        ...filter,
        name: search
      },
      pagination: {
        pageSkip: page * perPage,
        pageTake: perPage
      }
    }
  })

  const companyId = filter.companyId || user.companyId
  const isLoading = networkStatus === 1
  const totalCount: number = get(data, 'objectSecurities.pagination.totalCount')
  const objectSecurities: IObjectSecurity[] = get(data, 'objectSecurities.data', [])

  const createObjectSecurity = async (data: any, hideModal: () => void) => {
    try {
      await createObjectSecurityMutation({
        variables: {
          companyId,
          input: {
            active: data.active,
            description: data.description,
            name: data.name,
            objectEntityAPIName: getValueFromOption(data.object),
            securityProfileIds: getValuesFromOptions(data.profiles)
          }
        }
      })

      await refetch()
      hideModal()
    } catch (error) {
      handleError(error, 'ObjectSecurityAPI.createSecurityProfile')
    }
  }

  const deleteObjectSecurity = async (id: string, hideModal: () => void) => {
    try {
      await deleteObjectSecurityMutation({
        variables: {
          id,
          companyId
        }
      })

      await refetch()
      hideModal()
    } catch (error) {
      handleError(error, 'ObjectSecurityAPI.deleteObjectSecurity')
    }
  }

  const updateObjectSecurity = async (id: string, data: any, hideModal: () => void) => {
    try {
      await updateObjectSecurityMutation({
        variables: {
          id,
          companyId,
          input: {
            active: data.active,
            description: data.description,
            name: data.name,
            objectEntityAPIName: getValueFromOption(data.object),
            securityProfileIds: getValuesFromOptions(data.profiles)
          }
        }
      })

      await refetch()
      hideModal()
    } catch (error) {
      handleError(error, 'ObjectSecurityAPI.updateSecurityProfile')
    }
  }

  const value = {
    refetch,
    companyId,
    totalCount,
    updateObjectSecurity,
    createObjectSecurity,
    deleteObjectSecurity,
    data: objectSecurities,
    isLoading: networkStatus === 1,
    isRefetching: networkStatus === 2
  }

  return (
    <>
      <LoaderWithOverlay isLoading={isLoading} height={'calc(100% - 49px)'} />
      {!isLoading && <ObjectSecurityContext.Provider value={value}>{children}</ObjectSecurityContext.Provider>}
    </>
  )
}

export default ObjectSecurityAPI
